import { observer } from 'mobx-react-lite';

import { Button, XStack, YStack } from '@/components/ui';
import { Badge } from '@/components/ui/Badge';
import { Switch } from '@/components/ui/Switch';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

import {
  useProactiveAgentList,
  type ProactiveAgentActivity,
  type ProactiveAgentRow,
} from './use-proactive-agent-list';

import type { ReactNode } from 'react';

const ACTIVITY_LABELS: Record<ProactiveAgentActivity, string> = {
  active: 'Active',
  paused: 'Paused',
  stopped: 'Stopped',
  'not-started': 'Not started',
  'cannot-start': 'Cannot be started',
};

const activityTooltip = (row: ProactiveAgentRow): string => {
  switch (row.activity) {
    case 'active':
      if (row.chat?.kind === 'coding') {
        return 'This agent is running. It follows up on your coding sessions and can make cards for you.';
      }
      if (row.chat?.kind === 'email_triage') {
        return 'This agent is running. It sorts your new email and can make cards for you.';
      }
      return 'This agent is running. It gets a daily review while you use Littlebird, and can make cards for you.';
    case 'paused':
      return row.chat?.kind === 'email_triage'
        ? 'This agent is paused. Its routines do not run until it is resumed.'
        : 'This agent is paused. It gets no daily review, and its routines are paused until it is resumed.';
    case 'stopped':
      return 'This agent is stopped for good. Delete its chat to start it again.';
    case 'not-started':
      return 'This agent has no chat, so it does not run.';
    case 'cannot-start':
      return 'The agent this one was built on no longer exists, so it cannot start.';
  }
};

const defaultTooltip = (row: ProactiveAgentRow): string => {
  if (!row.canBeDefault) {
    return 'This agent does not take the messages your chats send to the proactive agent, so those messages reach no agent. Use Set as default on another agent.';
  }
  const meaning =
    'Messages your chats send to the proactive agent reach this agent once its first turn has finished.';
  return row.activity === 'active'
    ? meaning
    : `${meaning} While this agent is not running, those messages reach no agent.`;
};

const shownOnHomeTooltip = (
  row: ProactiveAgentRow,
  followsDefault: boolean
): string => {
  const meaning = "Your home screen on this device shows this agent's cards.";
  if (!followsDefault) {
    return `${meaning} This device keeps showing it, whichever agent is the default.`;
  }
  return row.isDefault
    ? `${meaning} This device follows My agents, including the default.`
    : `${meaning} This device follows My agents, including this explicitly selected agent.`;
};

const Chip = ({
  label,
  tooltip,
  variant = 'secondary',
}: {
  label: string;
  tooltip: string;
  variant?: 'secondary' | 'success-subtle';
}) => (
  <Tooltip label={tooltip} className="max-w-xs">
    <Badge variant={variant} size="xs" className="shrink-0">
      {label}
    </Badge>
  </Tooltip>
);

type ProactiveAgentListProps = {
  /** Extra controls for an agent, shown after the list's own buttons */
  renderActions?: (row: ProactiveAgentRow) => ReactNode;
  /** Marks the agents that ship with the app, which settings cannot delete */
  showBuiltIn?: boolean;
  className?: string;
};

/**
 * The user's proactive agents, each with chips for whether it runs, whether it is the default, and
 * whether this device's home screen shows its cards, and buttons to change the last two
 */
export const ProactiveAgentList = observer(function ProactiveAgentList({
  renderActions,
  showBuiltIn = false,
  className,
}: ProactiveAgentListProps) {
  const list = useProactiveAgentList();

  return (
    <YStack className={cn('w-full gap-5', className)}>
      {list.canSelect ? (
        <YStack className="gap-2">
          <XStack className="items-center justify-between gap-3">
            <span className="text-sm font-medium">
              Follow the default agent
            </span>
            <Switch
              aria-label="Follow the default agent"
              checked={list.followsReleasedAgent}
              disabled={list.isChanging}
              onCheckedChange={list.setFollowsReleasedAgent}
            />
          </XStack>
          <p className="text-xs text-foreground-muted">
            My agents run together and appear together in the filtered Feed.
            Follow the default to switch when a new version is released. Add
            other agents below to keep them alongside it. Removing one pauses it
            and keeps its history.
          </p>
        </YStack>
      ) : null}
      {list.rows.map((row) => {
        const actions = renderActions?.(row);
        return (
          <YStack
            key={row.agent}
            role="group"
            aria-label={row.agent}
            className="w-full gap-2"
          >
            <XStack className="items-center gap-3">
              <span
                className="min-w-0 flex-1 truncate text-sm font-medium"
                title={row.agent}
              >
                {row.agent}
              </span>
              {showBuiltIn && row.isBuiltIn ? (
                <Chip
                  label="Built-in"
                  tooltip="Ships with the app and cannot be deleted."
                />
              ) : null}
              {list.canSelect ? (
                <Switch
                  aria-label={`Include ${row.agent} in My agents`}
                  checked={row.isSelected}
                  disabled={
                    list.isChanging ||
                    (!row.isSelected &&
                      (!row.canStart || row.activity === 'stopped'))
                  }
                  onCheckedChange={(checked) =>
                    list.selectAgent(row.agent, checked)
                  }
                />
              ) : null}
            </XStack>
            <XStack wrap className="items-center gap-1.5">
              <Chip
                label={ACTIVITY_LABELS[row.activity]}
                tooltip={activityTooltip(row)}
                variant={
                  row.activity === 'active' ? 'success-subtle' : 'secondary'
                }
              />
              {row.isDefault ? (
                <Chip label="Default" tooltip={defaultTooltip(row)} />
              ) : null}
              {row.isShownOnHome ? (
                <Chip
                  label="Shown on home"
                  tooltip={shownOnHomeTooltip(row, list.homeFollowsDefault)}
                />
              ) : null}
            </XStack>
            {row.offersSetAsDefault || row.offersShowOnHome || actions ? (
              <XStack wrap className="items-center gap-2">
                {row.offersSetAsDefault ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    aria-label={`Set ${row.agent} as default`}
                    tooltip="Choose the default used for chat requests and Follow the default agent. It starts or resumes now. Other agents run only if selected in My agents."
                    tooltipOptions={{ className: 'max-w-xs' }}
                    disabled={list.isChanging}
                    onClick={() => list.setAsDefault(row.agent)}
                  >
                    Set as default
                  </Button>
                ) : null}
                {row.offersShowOnHome ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    aria-label={`Show ${row.agent} on home`}
                    tooltip="Show this agent's cards on the home screen of this device, whichever agent is the default. The default agent stays the same."
                    tooltipOptions={{ className: 'max-w-xs' }}
                    onClick={() => list.showOnHome(row.agent)}
                  >
                    Show on home
                  </Button>
                ) : null}
                {actions}
              </XStack>
            ) : null}
          </YStack>
        );
      })}
      {list.homeShowsNoAgent ? (
        <span className="text-xs text-foreground-muted">
          The home screen on this device shows no agent&apos;s cards.
        </span>
      ) : null}
      {list.offersShowNoAgentOnHome || list.offersFollowDefaultOnHome ? (
        <XStack wrap className="gap-2">
          {list.offersFollowDefaultOnHome ? (
            <Button
              variant="ghost"
              size="sm"
              tooltip="Show cards from My agents on this device, following the selection saved for your account."
              tooltipOptions={{ className: 'max-w-xs' }}
              onClick={list.followDefaultOnHome}
            >
              Show My agents on home
            </Button>
          ) : null}
          {list.offersShowNoAgentOnHome ? (
            <Button
              variant="ghost"
              size="sm"
              tooltip="Show no proactive agent's cards on the home screen of this device. Your own cards stay visible."
              tooltipOptions={{ className: 'max-w-xs' }}
              onClick={list.showNoAgentOnHome}
            >
              Show no agent on home
            </Button>
          ) : null}
        </XStack>
      ) : null}
    </YStack>
  );
});
